/**
 * Organization settings: register lookup, localization, in-place identity fields and the logo, each
 * saved on its own; read-only identity inside the master account's own books. Logic: `use-organization-settings`.
 */
import { useMemo } from 'react';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import {
  ExpandableTextarea,
  FieldEditor,
  Icon,
  InputWrapper,
  Paper,
  Select,
  TimezoneSelect
} from '@insio/ui-toolkit';
import { CountryCode } from '@insio/countries';
import { PARTY_REGISTRY_ENTRY_MAX_LENGTH } from '@insio/shared-types';
import { useOrganizationSettings } from './organization/use-organization-settings';
import { OrganizationLogoSection } from './organization/OrganizationLogoSection';
import { CompanyRegistryLookupRow } from '@/components/CompanyRegistryLookupRow';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import type { CurrencyCode } from '@/graphql';
import { isGraphqlLocale, LOCALE_KEYS } from '@/utils/graphql-locale';

/** A blank value means "not set", so it is stored as null rather than as an empty string. */
const textOrNull = (value: unknown): string | null => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
};

export function OrganizationSettings() {
  const {
    t,
    loading,
    error,
    account,
    mainParty,
    addr,
    preferences,
    noParty,
    identityReadOnly,
    saveAccountName,
    savePartyField,
    saveAddressField,
    savePartyPreferences,
    lookupCountry,
    hasCompanyId,
    registryLoading,
    registryError,
    registryApplied,
    applyRegistryLookup,
    clearRegistryFeedback
  } = useOrganizationSettings();

  const { showError } = useToastHelpers();
  const showLoading = useDelayedLoading(loading);
  const identityDisabled = noParty || identityReadOnly;

  /** Refused here rather than by the backend, so the message says what is wrong. */
  const saveRegistryEntry = async (value: unknown) => {
    const registryEntry = textOrNull(value);
    if (registryEntry && registryEntry.length > PARTY_REGISTRY_ENTRY_MAX_LENGTH) {
      showError(t('errors.registryEntryTooLong', { max: PARTY_REGISTRY_ENTRY_MAX_LENGTH }));
      // Rejecting keeps the editor open with the text, so nothing typed is lost.
      throw new Error('registryEntryTooLong');
    }
    await savePartyField({ registryEntry });
  };

  /** The organization always has an accounting currency; an empty choice is refused, never sent. */
  const saveAccountingCurrency = async (value: unknown) => {
    const currency = textOrNull(value);
    if (!currency) {
      showError(t('errors.accountingCurrencyRequired'));
      throw new Error('accountingCurrencyRequired');
    }
    await savePartyField({ accountingCurrencyCode: currency as CurrencyCode });
  };

  const localeOptions = useMemo(
    () => LOCALE_KEYS.map(key => ({ value: key, label: t(`localeOptions.${key}`) })),
    [t]
  );

  if (!account && loading) {
    return showLoading ? (
      <div className='text-sm text-foreground-muted py-lg'>{t('loading')}</div>
    ) : null;
  }

  if (error) {
    return <div className='text-sm text-error-text py-lg'>{t('error')}</div>;
  }

  return (
    <div className='space-y-lg'>
      <Paper showDivider={false} padding='none'>
        <Paper.Content>
          {identityReadOnly && (
            <div className='mb-lg flex gap-sm rounded-lg bg-info-bg px-md py-sm'>
              <Icon name='circle-info' size='sm' className='text-info-text mt-2xs shrink-0' />
              <p className='text-sm text-info-text'>{t('ownBooksIdentityNote')}</p>
            </div>
          )}

          {noParty && (
            <div className='mb-lg flex gap-sm rounded-lg bg-info-bg px-md py-sm'>
              <Icon name='circle-info' size='sm' className='text-info-text mt-2xs shrink-0' />
              <p className='text-sm text-info-text'>{t('feedback.noPartyToUpdate')}</p>
            </div>
          )}

          {/* ── REGISTER LOOKUP (CZ/SK) — first, it fills most of the fields below ── */}
          {lookupCountry && !identityReadOnly && (
            <div className='mb-lg space-y-md'>
              {!hasCompanyId && (
                <div className='flex gap-sm rounded-lg bg-info-bg px-md py-sm'>
                  <Icon name='circle-info' size='sm' className='text-info-text mt-2xs shrink-0' />
                  <p className='text-sm text-info-text'>
                    {t(`hints.registryInfo.${lookupCountry}`)}
                  </p>
                </div>
              )}
              <CompanyRegistryLookupRow
                id='org-registry-query'
                label={t(`registry.${lookupCountry}.label`)}
                placeholder={t(`registry.${lookupCountry}.placeholder`)}
                loading={registryLoading}
                error={registryError}
                applied={registryApplied}
                disabled={noParty}
                framed
                onApply={query => void applyRegistryLookup(query)}
                onQueryChange={clearRegistryFeedback}
              />
            </div>
          )}

          {/* ── LOCALIZATION SECTION ───────────────────────── */}
          <div className='mb-lg space-y-md'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-xl gap-y-md'>
              <InputWrapper
                label={t('fields.language')}
                htmlFor='org-language'
                labelSize='sm'
                required
              >
                <Select
                  id='org-language'
                  height='sm'
                  value={preferences?.locale ?? ''}
                  onChange={value => {
                    // The language is mandatory: an empty choice is ignored, never sent as null.
                    if (isGraphqlLocale(value)) savePartyPreferences({ locale: value });
                  }}
                  options={localeOptions}
                  isSearchable={false}
                  disabled={noParty}
                />
              </InputWrapper>

              <InputWrapper label={t('fields.timezone')} htmlFor='org-timezone' labelSize='sm'>
                <TimezoneSelect
                  id='org-timezone'
                  height='sm'
                  value={preferences?.timeZone ?? ''}
                  onChange={tz => savePartyPreferences({ timeZone: tz || null })}
                  unselectedOptionLabel='—'
                  disabled={noParty}
                />
              </InputWrapper>
            </div>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-x-xl gap-y-lg'>
            {/* ── LEFT COLUMN ─────────────────────────────────── */}
            <div className='space-y-md'>
              <InputWrapper label={t('fields.name')} htmlFor='account-name' labelSize='sm' required>
                <FieldEditor
                  id='account-name'
                  fieldType='text'
                  value={account?.name ?? ''}
                  onSave={value => saveAccountName((value as string) ?? '')}
                  disabled={identityReadOnly}
                />
              </InputWrapper>

              <InputWrapper label={t('fields.companyId')} htmlFor='org-ico' labelSize='sm'>
                <FieldEditor
                  id='org-ico'
                  fieldType='text'
                  value={mainParty?.companyId ?? ''}
                  onSave={v => savePartyField({ companyId: (v as string) ?? '' })}
                  disabled={identityDisabled}
                />
              </InputWrapper>

              <InputWrapper label={t('fields.taxId')} htmlFor='org-dic' labelSize='sm'>
                <FieldEditor
                  id='org-dic'
                  fieldType='text'
                  value={mainParty?.taxId ?? ''}
                  onSave={v => savePartyField({ taxId: (v as string) ?? '' })}
                  disabled={identityDisabled}
                />
              </InputWrapper>

              <InputWrapper
                label={t('fields.vatId')}
                htmlFor='org-ic-dph'
                labelSize='sm'
                hint={t('fields.vatIdHint')}
              >
                <FieldEditor
                  id='org-ic-dph'
                  fieldType='text'
                  value={mainParty?.vatId ?? ''}
                  onSave={v => savePartyField({ vatId: (v as string) ?? '' })}
                  disabled={identityDisabled}
                />
              </InputWrapper>

              <InputWrapper
                label={t('fields.registryEntry')}
                htmlFor='org-registry-entry'
                labelSize='sm'
                hint={t('fields.registryEntryHint')}
              >
                <FieldEditor
                  id='org-registry-entry'
                  fieldType='textarea'
                  value={mainParty?.registryEntry ?? ''}
                  onSave={saveRegistryEntry}
                  disabled={noParty}
                  render={({ value, onChange }) => (
                    <ExpandableTextarea
                      label={t('fields.registryEntry')}
                      aria-label={t('fields.registryEntry')}
                      // The editor sits inside FieldEditor, out of InputWrapper's reach; point it at
                      // the hint InputWrapper renders for htmlFor='org-registry-entry'.
                      aria-describedby='org-registry-entry-hint'
                      rows={3}
                      value={typeof value === 'string' ? value : ''}
                      onChange={onChange}
                    />
                  )}
                />
              </InputWrapper>

              <InputWrapper label={t('fields.street')} htmlFor='org-street' labelSize='sm'>
                <FieldEditor
                  id='org-street'
                  fieldType='text'
                  value={addr?.streetName ?? ''}
                  onSave={v => saveAddressField('streetName')((v as string) ?? '')}
                  disabled={identityDisabled}
                />
              </InputWrapper>

              <InputWrapper
                label={t('fields.buildingNumber')}
                htmlFor='org-building'
                labelSize='sm'
              >
                <FieldEditor
                  id='org-building'
                  fieldType='text'
                  value={addr?.buildingNumber ?? ''}
                  onSave={v => saveAddressField('buildingNumber')((v as string) ?? '')}
                  disabled={identityDisabled}
                />
              </InputWrapper>

              <InputWrapper label={t('fields.city')} htmlFor='org-city' labelSize='sm'>
                <FieldEditor
                  id='org-city'
                  fieldType='text'
                  value={addr?.city ?? ''}
                  onSave={v => saveAddressField('city')((v as string) ?? '')}
                  disabled={identityDisabled}
                />
              </InputWrapper>

              <InputWrapper label={t('fields.postalCode')} htmlFor='org-postal' labelSize='sm'>
                <FieldEditor
                  id='org-postal'
                  fieldType='text'
                  value={addr?.postalCode ?? ''}
                  onSave={v => saveAddressField('postalCode')((v as string) ?? '')}
                  disabled={identityDisabled}
                />
              </InputWrapper>
            </div>

            {/* ── RIGHT COLUMN ─────────────────────────────────── */}
            <div className='space-y-md'>
              <InputWrapper
                label={t('fields.accountingCurrencyCode')}
                htmlFor='org-accounting-currency'
                labelSize='sm'
                hint={t('fields.accountingCurrencyCodeHint')}
                required
              >
                <FieldEditor
                  id='org-accounting-currency'
                  fieldType='currency'
                  value={mainParty?.accountingCurrencyCode ?? ''}
                  disabled={noParty}
                  onSave={saveAccountingCurrency}
                />
              </InputWrapper>

              <InputWrapper
                label={t('fields.reportingCurrencyCode')}
                htmlFor='org-reporting-currency'
                labelSize='sm'
                hint={t('fields.reportingCurrencyCodeHint')}
              >
                <FieldEditor
                  id='org-reporting-currency'
                  fieldType='currency'
                  value={mainParty?.reportingCurrencyCode ?? ''}
                  // Left empty, reports use the accounting currency, so that is what the field shows.
                  placeholder={mainParty?.accountingCurrencyCode ?? undefined}
                  disabled={noParty}
                  onSave={v => {
                    const currency = ((v as string) ?? '').trim();
                    return savePartyField({
                      reportingCurrencyCode: currency ? (currency as CurrencyCode) : null
                    });
                  }}
                />
              </InputWrapper>

              <InputWrapper label={t('fields.country')} htmlFor='org-country' labelSize='sm'>
                <FieldEditor
                  id='org-country'
                  fieldType='country'
                  value={addr?.countryCode ?? ''}
                  disabled={identityDisabled}
                  onSave={v =>
                    savePartyField({
                      mainAddress: { countryCode: v as CountryCode | null }
                    })
                  }
                />
              </InputWrapper>

              <InputWrapper label={t('fields.phone')} htmlFor='org-phone' labelSize='sm'>
                <FieldEditor
                  id='org-phone'
                  fieldType='text'
                  value={mainParty?.mainContact?.phone ?? ''}
                  disabled={identityDisabled}
                  onSave={v => savePartyField({ mainContact: { phone: textOrNull(v) } })}
                />
              </InputWrapper>

              <InputWrapper label={t('fields.email')} htmlFor='org-email' labelSize='sm'>
                <FieldEditor
                  id='org-email'
                  fieldType='text'
                  value={mainParty?.mainContact?.email ?? ''}
                  disabled={identityDisabled}
                  onSave={v => savePartyField({ mainContact: { email: textOrNull(v) } })}
                />
              </InputWrapper>

              {account && (
                <OrganizationLogoSection accountId={account.id} logo={account.logo ?? null} />
              )}
            </div>
          </div>
        </Paper.Content>
      </Paper>
    </div>
  );
}
